import { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button, Card, Form, Skeleton, Steps } from 'antd';
import { useFormContext } from './Context';
import { Config, Cron, Header, Parameters } from './Forms';
import { splitConfigByParams, parseConfig, cleanUpEditedTask, cleanUpNewTask, extractConfigDefinitionTypesMap, extractConfigDefinitionSubTypesMap } from './helpers';

const TaskForm = ({ plugins, ...props }) => {
  const orchestrator = plugins['weaver-orchestrator'];

  const [isLoading, setIsLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  const { formValues, updateFormValues, cronMap } = useFormContext();
  const { params: { spaceId, taskId } } = useRouteMatch();
  const history = useHistory();

  const [headerForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [cronForm] = Form.useForm();
  const [parametersForm] = Form.useForm();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      if (taskId !== "new") {
        await getAndSetFormValues(taskId);
      }
      setIsLoading(false);
    })();
  }, []);

  const functionalityProperties = formValues.header?.functionality?.configDefinition?.properties || {};
  const parameterProperties = Object.entries(functionalityProperties).filter(([key, configProps]) => {
    return key === 'file' || configProps.subType === 'json5-with-vars';
  });
  const showParamPanel = parameterProperties.length > 0;

  const forms = useMemo(() => [
    {
      Component: Header,
      componentProps: { key: 'header', title: 'Header', form: headerForm },
    },
    {
      Component: Config,
      componentProps: { key: 'config', title: 'Config', form: configForm },
    },
    {
      Component: Cron,
      componentProps: { key: 'crons', title: 'Cron', form: cronForm },
    },
    {
      Component: Parameters,
      componentProps: { key: 'parameters', title: 'Parameters', form: parametersForm, disabled: !showParamPanel },
    },
  ], [headerForm, configForm, cronForm, parametersForm, showParamPanel]);

  const getAndSetFormValues = async (scheduleUid) => {
    const task = JSON.parse(await orchestrator.getSchedule({ scheduleUid }));
    const functionalities = JSON.parse(await orchestrator.getFunctionalities());
    const functionality = functionalities.find(
      (f) => f.name === task.functionalityName && f.version === task.functionalityVersion
    );

    const headerValues = {
      name: task.name,
      branch: task.branch,
      functionality,
    };

    const functionalityProperties = functionality?.configDefinition?.properties || {};
    const { config, parameters } = splitConfigByParams(task.config || {}, functionalityProperties);

    updateFormValues({
      header: headerValues,
      crons: task.crons,
      config,
      parameters,
    });
  };

  const next = () => {
    setCurrent((prev) => Math.min(prev + 1, forms.length - 1));
  }

  const prev = () => {
    setCurrent((prev) => Math.max(prev - 1, 0))
  };

  const CurrentComponent = forms[current]?.Component;
  const currentProps = forms[current]?.componentProps;

  const handleSendCronFromData = async (fields, scheduleUid) => {
    const parsed = fields?.map((field) => {
      const { firstRun, runOnce, cron, uid } = field
      return {
        cronUid: uid,
        cron: cronMap[uid]?.disabled === true ? {} : cron,
        firstRun: cronMap[uid]?.disabled === true ? -1 : firstRun?.utc().valueOf() / 1000,
        runOnce
      }
    })

    for (let item in parsed) {
      if (parsed[item].cronUid !== undefined) {
        await orchestrator.updateCron(parsed[item])
      } else {
        await orchestrator.addCron({ ...parsed[item], scheduleUid })
      }
    }
  }

  const onFinish = async ({ header, config, crons, parameters }) => {
    try {
      const configDefTypesMap = extractConfigDefinitionTypesMap(header.functionality.configDefinition);
      const configDefSubTypesMap = extractConfigDefinitionSubTypesMap(header.functionality.configDefinition)

      const cleanedConfig = {
        ..._.omit(config, Object.keys(parameters)),
        ...parameters
      };

      console.log({ cleanedConfig})

      for (const key in configDefSubTypesMap) {
        if (configDefSubTypesMap[key] === 'json5-with-vars' && cleanedConfig[key + '-vars'] !== undefined) {
          cleanedConfig[key] = Weaver.Attribute.updateWithVars(cleanedConfig[key], cleanedConfig[key + '-vars'])
          delete cleanedConfig[key + '-vars']
        }
      }

      const parsedConfig = await parseConfig(cleanedConfig, configDefTypesMap);
      let cleaned, scheduleUid;

      if (taskId !== 'new') {
        scheduleUid = taskId
        cleaned = cleanUpEditedTask({ scheduleUid, config: parsedConfig });
        await orchestrator.updateSchedule({ scheduleUid, name: header.name })
        await orchestrator.updateConfiguration(cleaned)
      } else {
        const parsedHeader = {
          name: header.name,
          branch: header.branch,
          functionalityVersion: header.functionality.version,
          functionalityName: header.functionality.configDefinition.name,
        };
        cleaned = cleanUpNewTask({ ...parsedHeader, config: parsedConfig });
        scheduleUid = await orchestrator.schedule(cleaned)
      }

      await handleSendCronFromData(crons.crons, scheduleUid)

      history.push({
        pathname: `/${spaceId}/pipelines/tasks`,
        state: scheduleUid
      })
    } catch (e) {
      console.error(e)
    }
  };

  const saveDisabled = formValues.header?.functionality === undefined;
  const showPrev = current !== 0
  const showNext = !(current === 2 && !showParamPanel) && current !== forms.length - 1

  return (
    <Card className="task-form-container" style={{ margin: '20px', height: '100%' }}>
      <Steps
        type="navigation"
        current={current}
        onChange={setCurrent}
        size="small"
      >
        <Steps.Step icon={<></>} title="Header" />
        <Steps.Step icon={<></>} title="Config" />
        <Steps.Step icon={<></>} title="Crons" />
        <Steps.Step icon={<></>} title="Default Values" disabled={!showParamPanel} />
      </Steps>
      <div className="forms">
        <Skeleton active loading={isLoading}>
          {CurrentComponent && <CurrentComponent {...currentProps} next={next} prev={prev} taskId={taskId} />}
        </Skeleton>
      </div>
      <div className="action-buttons">
        <Button onClick={() => history.goBack()}>Cancel</Button>
        {showPrev ? <Button onClick={prev}>Previous</Button> : <Button className="placeholder-button" />}
        {showNext ? <Button onClick={next}>Next</Button> : <Button className="placeholder-button" />}
        <Button
          type="primary"
          disabled={saveDisabled}
          onClick={() => onFinish({ ...formValues, [currentProps.key]: currentProps.form.getFieldsValue() })}
        >
          Save
        </Button>
      </div>
    </Card>
  );
};

const mapStateToProps = ({ explore: { plugins } }) => ({ plugins })
export default connect(mapStateToProps)(TaskForm)
